import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import MuiLink from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import {
  EmptyState,
  Link,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  age,
  CheckIcon,
  githubUrl,
  PHASE_COLOR,
  REVIEW_ENV_REPOSITORY_ANNOTATION,
  usePolledReviewEnvs,
} from './common';
import { Check, ReviewEnv } from './types';

function workloadsTooltip(w: NonNullable<ReviewEnv['workloads']>): string {
  if (w.stalled.length > 0) return `Rollout stalled: ${w.stalled.join(', ')}`;
  if (w.notReady.length > 0) return `Not available: ${w.notReady.join(', ')}`;
  return 'All deployments available';
}

/**
 * All green collapses to one line (names in the tooltip); otherwise only the
 * checks that need attention are listed, so rows stay one line high.
 */
function ChecksCell({ checks }: { checks: Check[] }) {
  if (checks.length === 0) return <span>–</span>;
  const open = checks.filter(c => c.status !== 'ok');
  if (open.length === 0) {
    return (
      <Tooltip title={checks.map(c => c.name).join(', ')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CheckIcon status="ok" />
          <Typography variant="caption">all {checks.length} ok</Typography>
        </Box>
      </Tooltip>
    );
  }
  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {open.map(c => (
        <Tooltip key={c.name} title={c.message ?? c.status}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <CheckIcon status={c.status} />
            <Typography variant="caption">{c.name}</Typography>
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
}

/** List of a repo's review envs; a row opens its detail page. */
export const ReviewEnvsTable = () => {
  const { entity } = useEntity();
  const navigate = useNavigate();
  const repository =
    entity.metadata.annotations?.[REVIEW_ENV_REPOSITORY_ANNOTATION] ?? '';
  const githubSlug = entity.metadata.annotations?.['github.com/project-slug'];
  const { data, error, loading } = usePolledReviewEnvs<{
    items: ReviewEnv[];
    fetchedAt: string;
  }>(encodeURIComponent(repository));
  const items = data?.items;
  const now = Date.now();

  if (loading) return <Progress />;
  if (error && !items) return <ResponseErrorPanel error={error} />;
  if (!items || items.length === 0) {
    return (
      <EmptyState
        missing="data"
        title="No review environments"
        description={`No review environments for ${repository} are running right now.`}
      />
    );
  }

  const prLink = (pr: number, repo: string) => {
    const url = githubUrl(githubSlug, repo, pr);
    if (!url) return <span>#{pr}</span>;
    return (
      <MuiLink
        href={url}
        target="_blank"
        rel="noopener"
        onClick={e => e.stopPropagation()}
      >
        #{pr}
      </MuiLink>
    );
  };

  const columns: TableColumn<ReviewEnv>[] = [
    {
      title: 'Env',
      field: 'name',
      render: env => (
        <Box>
          <Link to={env.name} onClick={e => e.stopPropagation()}>
            {env.pr ? `PR ${env.pr}` : env.name}
          </Link>
          {env.linkedTo && (
            <Typography variant="caption" display="block">
              with {env.linkedTo.repository}{' '}
              {prLink(env.linkedTo.pr, env.linkedTo.repository)}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      title: 'PR',
      field: 'pr',
      width: '80px',
      render: env => (env.pr ? prLink(env.pr, env.repository) : '–'),
    },
    {
      title: 'Status',
      field: 'phase',
      render: env => (
        <Tooltip title={env.message ?? ''}>
          <Chip
            size="small"
            label={env.phase}
            color={PHASE_COLOR[env.phase]}
            variant={env.phase === 'orphaned' ? 'outlined' : 'filled'}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Checks',
      sorting: false,
      render: env => <ChecksCell checks={env.checks} />,
    },
    {
      title: 'Deployments',
      sorting: false,
      render: env =>
        env.workloads ? (
          <Tooltip title={workloadsTooltip(env.workloads)}>
            <span>
              {env.workloads.ready}/{env.workloads.total}
            </span>
          </Tooltip>
        ) : (
          '–'
        ),
    },
    {
      title: 'URL',
      field: 'url',
      render: env =>
        env.url && env.phase !== 'orphaned' ? (
          <MuiLink
            href={env.url}
            target="_blank"
            rel="noopener"
            onClick={e => e.stopPropagation()}
          >
            {env.url.replace(/^https:\/\//, '')}
          </MuiLink>
        ) : (
          '–'
        ),
    },
    { title: 'Author', field: 'author', render: env => env.author ?? '–' },
    {
      title: 'Age',
      field: 'createdAt',
      render: env => age(env.createdAt, now),
    },
  ];

  const counts = items.reduce<Record<string, number>>((acc, env) => {
    acc[env.phase] = (acc[env.phase] ?? 0) + 1;
    return acc;
  }, {});
  const summary = Object.entries(counts)
    .map(([phase, n]) => `${n} ${phase}`)
    .join(' · ');
  const asOf = data?.fetchedAt
    ? ` · as of ${new Date(data.fetchedAt).toLocaleTimeString()}`
    : '';

  return (
    <Table<ReviewEnv>
      title={`Review environments — ${repository}`}
      subtitle={`${summary}${asOf}${error ? ' · refresh failing' : ''}`}
      options={{ paging: false, search: true, padding: 'dense' }}
      columns={columns}
      data={items}
      onRowClick={(_e, env) => env && navigate(env.name)}
    />
  );
};
